/**
 * PasskeyManager — user portal.
 * Renders the "Passkeys" card on the account page: enroll a passkey (only when
 * none exists yet) and list / remove enrolled passkeys. Passkeys here are used
 * for passwordless sign-in.
 */

import { useCallback, useEffect, useState } from 'react';
import { passkeyService } from '@/api/services';
import { usePasskey, useAuth, useOptimisticList } from '@/hooks';
import { getPasskeyErrorMessage } from '@/lib/errors/passkey-error';
import type { EnrolledPasskey } from '@/api/models';
import { Button } from '@/components/common/Button/Button';
import { Spinner } from '@/components/common/Spinner/Spinner';

type Feedback = { type: 'success' | 'error'; message: string } | null;

export function PasskeyManager() {
  const passkey = usePasskey();
  const { notePasskeyEnrolled } = useAuth();
  const [rawPasskeys, setRawPasskeys] = useState<EnrolledPasskey[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [deviceName, setDeviceName] = useState('');

  // Optimistic add/remove keyed by credentialId — same pattern used for
  // Contacts/GlobalGeofences, so a just-enrolled/just-removed passkey doesn't
  // flicker while `loadPasskeys()`'s refetch races the backend write.
  const passkeyKey = useCallback((pk: EnrolledPasskey) => pk.credentialId, []);
  const { mergedList: passkeys, addOptimistic, removeOptimistic } = useOptimisticList(rawPasskeys, passkeyKey);

  const loadPasskeys = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await passkeyService.list();
      setRawPasskeys(resp.passkeys);
    } catch (err) {
      setFeedback({ type: 'error', message: getPasskeyErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  const trimmedDeviceName = deviceName.trim();

  const handleEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedDeviceName) return;
    setFeedback(null);
    try {
      const result = await passkey.enrollPasskey(trimmedDeviceName);
      // Mark this device as having a passkey so the post-login nudge stops here.
      notePasskeyEnrolled();
      addOptimistic({
        credentialId: result.credentialId,
        deviceName: result.deviceName,
        createdAt: new Date().toISOString(),
        lastUsedAt: new Date().toISOString(),
      });
      setFeedback({ type: 'success', message: `Passkey "${result.deviceName}" added.` });
      setDeviceName('');
      await loadPasskeys();
    } catch (err) {
      setFeedback({ type: 'error', message: getPasskeyErrorMessage(err) });
    }
  };

  const handleDelete = async (credentialId: string) => {
    if (!confirm('Remove this passkey? You will no longer be able to sign in with it.')) return;
    setFeedback(null);
    try {
      await passkeyService.remove({ credentialId });
      removeOptimistic(credentialId);
      await loadPasskeys();
    } catch (err) {
      setFeedback({ type: 'error', message: getPasskeyErrorMessage(err) });
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Passkeys</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Sign in without a password using your fingerprint, face, or device PIN.
        </p>
      </div>

      <div className="space-y-5 px-5 py-4">
        {!passkey.isSupported && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
            This browser does not support passkeys. Use a recent version of Chrome, Edge, Safari, or Firefox.
          </div>
        )}

        {feedback && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              feedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
            }`}
          >
            {feedback.message}
          </div>
        )}

        {/* Offer enrollment on every device — a user can register one passkey per
            device (phone, laptop, etc.); the list below shows all of them. */}
        {passkey.isSupported && !loading && (
          <form onSubmit={handleEnroll} className="space-y-3">
            <div>
              <label htmlFor="passkey-device-name" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Device name
              </label>
              <input
                id="passkey-device-name"
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. My Phone, Work Laptop"
                required
                disabled={passkey.isBusy}
                className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Helps you tell your passkeys apart later — e.g. which one is your phone vs. this
                browser.
              </p>
            </div>
            <Button type="submit" loading={passkey.isBusy} disabled={passkey.isBusy || !trimmedDeviceName}>
              Add Passkey
            </Button>
          </form>
        )}

        {/* Enrolled passkeys */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Your passkeys</h3>
          {loading ? (
            <Spinner size="sm" />
          ) : passkeys.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No passkeys enrolled yet.</p>
          ) : (
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 dark:divide-navy-700 dark:border-navy-700">
              {passkeys.map((pk) => (
                <li key={pk.credentialId} className="flex items-center justify-between px-3 py-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-white">{pk.deviceName}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Added {new Date(pk.createdAt).toLocaleString()}
                      {pk.lastUsedAt && pk.lastUsedAt !== pk.createdAt && (
                        <> · Last used {new Date(pk.lastUsedAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(pk.credentialId)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasskeyManager;
